import { useRef, useState } from 'react'
import { Icon, IconName } from '@/components/Icon'
import { Popover } from '@/components/Popover'
import { Button } from '@/components/Button'
import { ReactNode } from 'react'

interface FilterButtonBaseProps {
	label: string
	icon?: IconName
	children: ReactNode
	className?: string
}

type FilterButtonProps = FilterButtonBaseProps & {
	activeLabel?: string
	onClear?: () => void
	additionalCount?: number
	open?: boolean
	onOpenChange?: (open: boolean) => void
	minWidth?: number
}

export function FilterButton ({
	label,
	icon,
	activeLabel,
	onClear,
	additionalCount,
	children,
	className = '',
	open: controlledOpen,
	onOpenChange,
	minWidth = 304
}: FilterButtonProps) {
	const [internalOpen, setInternalOpen] = useState(false)
	const isControlled = controlledOpen !== undefined
	const open = isControlled ? controlledOpen : internalOpen

	const setOpen = (nextOpen: boolean) => {
		if (!isControlled) setInternalOpen(nextOpen)
		onOpenChange?.(nextOpen)
	}

	const triggerRef = useRef<HTMLButtonElement>(null)
	const isActive = !!activeLabel
	const displayLabel = activeLabel || label
	const triggerClasses = [
		'spaces-filter-trigger spaces:inline-flex spaces:items-center spaces:justify-center spaces:gap-4 spaces:rounded-12 spaces:text-16/24 spaces:border spaces:shrink-0 spaces:cursor-pointer spaces:transition-colors spaces:focus-visible:outline-canvas-base spaces:focus-visible:ring-2 spaces:focus-visible:ring-brand-base spaces:focus-visible:ring-offset-2',
		isActive
			// Right padding reserves room for the Clear button overlaid by the anchor.
			? 'spaces:pl-15 spaces:pr-35 spaces:py-7 spaces:border-transparent spaces:bg-brand-base spaces:text-brand-on-base spaces:group-hover:bg-brand-hover'
			: [
					'spaces:px-15 spaces:py-7 spaces:border-canvas-2 spaces:text-content-2 spaces:hover:bg-canvas-4',
					open ? 'spaces:bg-canvas-4' : 'spaces:bg-canvas-base'
			  ]
					.filter(Boolean)
					.join(' '),
		isActive && 'spaces-active',
		className
	]
		.filter(Boolean)
		.join(' ')

	return (
		<Popover
			placement='bottom'
			align='start'
			open={open}
			onOpenChange={setOpen}
			minWidth={minWidth}
			maxHeight={null}
			padding={0}
			ariaLabel={label}
			containerClassName='spaces:group'
			trigger={
				<button
					ref={triggerRef}
					type='button'
					className={triggerClasses}
				>
					<span className='spaces:flex spaces:gap-8'>
						{icon && <Icon name={icon} size={24} />}
						<span className='spaces:flex spaces:gap-4 spaces:items-center'>
							<span className={`spaces:whitespace-nowrap spaces:max-w-[160px] spaces:truncate`}>
								{displayLabel}
							</span>
							{isActive && !!additionalCount && additionalCount > 0 && (
								<span className='spaces:whitespace-nowrap spaces:text-16 spaces:leading-24'>
									+{additionalCount}
								</span>
							)}
						</span>
					</span>
				</button>
			}
			triggerAdornment={
				isActive && (
					<Button
						appearance='solid'
						color='brand'
						size='xs'
						iconOnly='X'
						aria-label={`Clear ${label} filter`}
						spacesClass='spaces-filter-clear'
						className='spaces:absolute spaces:right-8 spaces:top-1/2 spaces:-translate-y-1/2 spaces:group-hover:bg-brand-hover'
						onClick={() => {
							// This button unmounts once cleared, so hand focus back to the trigger.
							triggerRef.current?.focus()
							onClear?.()
						}}
					/>
				)
			}
		>
			{children}
		</Popover>
	)
}
